import React, { Dispatch, SetStateAction, useState } from 'react';
import styled, { StyledComponentPropsWithRef } from 'styled-components';
import { openAlertModal } from '~/javascripts/admin/actions/alert-modal';
import { fetchCampaigns, updateCampaign } from '~/javascripts/admin/actions/campaign';
import { COLOR } from '~/javascripts/admin/constants/styles';
import { useAppDispatch } from '~/javascripts/admin/hooks/useApp';
import TextBox from '../../../atoms/TextBox';
import ApiResults from '../../../molecules/ApiResults';

type Campaigns = Awaited<ReturnType<typeof fetchCampaigns>>;
type Campaign = Campaigns['rows'][number];
type Message = StyledComponentPropsWithRef<typeof ApiResults>['messages'];

type Props = {
  campaign: Campaign;
  setApiResult: Dispatch<SetStateAction<Message | undefined>>;
  onUpdatePriority: (priority: number) => void;
} & StyledComponentPropsWithRef<typeof TextBox>;

const PriorityInputTextBox = styled(TextBox)<{ isModified?: boolean }>`
  background: ${(props) => (props.isModified ? COLOR.alert : 'white')};
  width: 100px;
`;

const PriorityInput = ({ campaign, setApiResult, onUpdatePriority, ...rest }: Props) => {
  const dispatch = useAppDispatch();
  const [isPriorityModified, setIsPriorityModified] = useState(false);
  const [updatedPriority, setUpdatedPriority] = useState(campaign.priority.toString());

  const handleUpdatePriority = (priority: string) => {
    const newPriority = parseInt(priority, 10);

    if (
      campaign.priority !== newPriority &&
      !(
        campaign.description === '条件付きキャンペーン' &&
        campaign.private_money.organization.code.startsWith('univcoop-')
      )
    ) {
      setIsPriorityModified(true);

      dispatch(
        openAlertModal({
          body: <p>{campaign.name}を更新してもよろしいですか？</p>,
          confirmText: '実行',
          onConfirm: () => {
            updateCampaign(campaign.id, { priority: newPriority })
              .then((res) => {
                onUpdatePriority(res.priority);

                setApiResult([
                  {
                    text: `${res.name}は更新されました`,
                    isError: false,
                  },
                ]);
              })
              .catch((err) => {
                setApiResult([
                  {
                    text: err.message,
                    isError: true,
                  },
                ]);
              })
              .finally(() => {
                setTimeout(() => setApiResult(undefined), 4000);
              });
            setIsPriorityModified(false);
          },
          cancelText: 'キャンセル',
          onCancel: () => {
            setUpdatedPriority(campaign.priority.toString());
            setIsPriorityModified(false);
          },
        })
      );
    } else {
      setIsPriorityModified(false);
    }
  };

  return (
    <PriorityInputTextBox
      size={2}
      syncValue={updatedPriority.toString()}
      onChange={(e) => setUpdatedPriority(e.target.value)}
      onBlur={(e) => {
        handleUpdatePriority((e.target as HTMLInputElement).value);
      }}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          handleUpdatePriority((e.target as HTMLInputElement).value);
        }
      }}
      isModified={isPriorityModified}
      readOnly={campaign.is_disabled}
      align="center"
      {...rest}
    />
  );
};

export default styled(PriorityInput)``;
